import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosHttp from "services/axiosHttp";
import axios from "axios";

const initialState = {
	// Media list
	mediaList: [],
	media: null,
	isLoading: false,
	error: null,

	// Upload state
	upload: {
		isUploading: false,
		progress: 0,
		currentStep: "", // "requesting", "uploading", "saving"
		error: null,
		presignedData: null,
	},

	// Media types
	mediaTypes: [],
	mediaTypesLoading: false,

	// Pagination
	pagination: {
		currentPage: 1,
		pageSize: 10,
		total: 0,
	},
};

// Get Presigned URL for S3 Upload
export const getPresignedUrl = createAsyncThunk(
	"lmsMedia/getPresignedUrl",
	async ({ fileName, fileType, folder = "lms/media" }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.post("lms/media/presigned-url", {
				fileName,
				fileType,
				folder,
			});
			return response.data.data;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message);
		}
	},
);

// Upload File to S3
export const uploadToS3 = createAsyncThunk("lmsMedia/uploadToS3", async ({ uploadUrl, file, onProgress }, { rejectWithValue }) => {
	try {
		await axios.put(uploadUrl, file, {
			headers: {
				"Content-Type": file.type,
			},
			onUploadProgress: (progressEvent) => {
				const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
				if (onProgress) {
					onProgress(percentCompleted);
				}
			},
		});
		return true;
	} catch (error) {
		return rejectWithValue(error.message || "Failed to upload to S3");
	}
});

// Create Media (Save metadata to database)
export const createMedia = createAsyncThunk("lmsMedia/createMedia", async (mediaData, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post("lms/media", mediaData);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Fetch All Media (with pagination)
export const fetchMediaList = createAsyncThunk("lmsMedia/fetchMediaList", async ({ page, limit, filters } = {}, { rejectWithValue }) => {
	try {
		const params = new URLSearchParams({
			page: page ?? 1,
			limit: limit ?? 10,
			course_id: filters?.course_id ?? "",
			section_id: filters?.section_id ?? "",
			media_type_id: filters?.media_type_id ?? "",
			search: filters?.search ?? "",
		});

		const response = await axiosHttp.get(`lms/media?${params}`);
		return response.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Fetch Media by ID
export const fetchMediaById = createAsyncThunk("lmsMedia/fetchMediaById", async ({ mediaId }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`lms/media/${mediaId}`);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Fetch Media by Course
export const fetchMediaByCourse = createAsyncThunk("lmsMedia/fetchMediaByCourse", async ({ courseId }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`lms/media/course/${courseId}`);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Fetch Media by Section
export const fetchMediaBySection = createAsyncThunk("lmsMedia/fetchMediaBySection", async ({ sectionId }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`lms/media/section/${sectionId}`);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Update Media
export const updateMedia = createAsyncThunk("lmsMedia/updateMedia", async ({ mediaId, ...updateData }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.put(`lms/media/${mediaId}`, updateData);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Delete Media
export const deleteMedia = createAsyncThunk("lmsMedia/deleteMedia", async ({ mediaId }, { rejectWithValue }) => {
	try {
		await axiosHttp.delete(`lms/media/${mediaId}`);
		return mediaId;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Fetch Media Types
export const fetchMediaTypes = createAsyncThunk("lmsMedia/fetchMediaTypes", async (_, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get("lms/media/types");
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Complete Upload Flow (Combines all steps)
export const uploadMediaComplete = createAsyncThunk(
	"lmsMedia/uploadMediaComplete",
	async ({ file, metadata, onProgress }, { dispatch, rejectWithValue }) => {
		try {
			// Step 1: Get presigned URL
			const presignedResult = await dispatch(
				getPresignedUrl({
					fileName: file.name,
					fileType: file.type,
					folder: "lms/media",
				}),
			).unwrap();

			// Step 2: Upload to S3
			await dispatch(
				uploadToS3({
					uploadUrl: presignedResult.uploadUrl,
					file,
					onProgress,
				}),
			).unwrap();

			// Step 3: Save metadata
			const mediaResult = await dispatch(
				createMedia({
					...metadata,
					file_url: presignedResult.s3Key,
					file_size: file.size.toString(),
				}),
			).unwrap();

			return mediaResult;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

const mediaSlice = createSlice({
	name: "lmsMedia",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
			state.upload.error = null;
		},
		resetUploadState: (state) => {
			state.upload = {
				isUploading: false,
				progress: 0,
				currentStep: "",
				error: null,
				presignedData: null,
			};
		},
		setUploadProgress: (state, action) => {
			state.upload.progress = action.payload;
		},
		setUploadStep: (state, action) => {
			state.upload.currentStep = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			// Get Presigned URL
			.addCase(getPresignedUrl.pending, (state) => {
				state.upload.currentStep = "requesting";
				state.upload.error = null;
			})
			.addCase(getPresignedUrl.fulfilled, (state, action) => {
				state.upload.presignedData = action.payload;
				state.upload.progress = 10;
			})
			.addCase(getPresignedUrl.rejected, (state, action) => {
				state.upload.error = action.payload;
				state.upload.currentStep = "";
			})

			// Upload to S3
			.addCase(uploadToS3.pending, (state) => {
				state.upload.currentStep = "uploading";
				state.upload.progress = 10;
			})
			.addCase(uploadToS3.fulfilled, (state) => {
				state.upload.progress = 90;
			})
			.addCase(uploadToS3.rejected, (state, action) => {
				state.upload.error = action.payload;
				state.upload.currentStep = "";
			})

			// Create Media
			.addCase(createMedia.pending, (state) => {
				state.upload.currentStep = "saving";
				state.upload.progress = 90;
			})
			.addCase(createMedia.fulfilled, (state, action) => {
				state.upload.progress = 100;
				state.upload.currentStep = "complete";
				state.mediaList.unshift(action.payload);
			})
			.addCase(createMedia.rejected, (state, action) => {
				state.upload.error = action.payload;
				state.upload.currentStep = "";
			})

			// Complete Upload Flow
			.addCase(uploadMediaComplete.pending, (state) => {
				state.upload.isUploading = true;
				state.upload.progress = 0;
				state.upload.error = null;
			})
			.addCase(uploadMediaComplete.fulfilled, (state) => {
				state.upload.isUploading = false;
				state.upload.progress = 100;
				state.upload.currentStep = "complete";
			})
			.addCase(uploadMediaComplete.rejected, (state, action) => {
				state.upload.isUploading = false;
				state.upload.error = action.payload;
				state.upload.currentStep = "";
			})

			// Fetch Media List
			.addCase(fetchMediaList.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchMediaList.fulfilled, (state, action) => {
				state.isLoading = false;
				// Handle the actual backend response structure
				if (action.payload.data && Array.isArray(action.payload.data)) {
					// Backend returns { success: true, data: [...], total, page, limit }
					state.mediaList = action.payload.data;
					state.pagination = {
						currentPage: action.payload.page || 1,
						pageSize: action.payload.limit || 10,
						total: action.payload.total || 0,
					};
				} else if (Array.isArray(action.payload)) {
					// If response is just an array
					state.mediaList = action.payload;
					state.pagination.total = action.payload.length;
				} else if (action.payload.media) {
					// If response has media property
					state.mediaList = action.payload.media;
					if (action.payload.pagination) {
						state.pagination = {
							currentPage: action.payload.pagination.page || action.payload.pagination.currentPage || 1,
							pageSize: action.payload.pagination.limit || action.payload.pagination.pageSize || 10,
							total: action.payload.pagination.total || action.payload.pagination.totalItems || 0,
						};
					} else if (action.payload.total !== undefined) {
						state.pagination.total = action.payload.total;
					}
				} else {
					// Fallback
					state.mediaList = action.payload;
				}
			})
			.addCase(fetchMediaList.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Fetch Media by ID
			.addCase(fetchMediaById.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(fetchMediaById.fulfilled, (state, action) => {
				state.isLoading = false;
				state.media = action.payload;
			})
			.addCase(fetchMediaById.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Fetch Media by Course
			.addCase(fetchMediaByCourse.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(fetchMediaByCourse.fulfilled, (state, action) => {
				state.isLoading = false;
				state.mediaList = action.payload;
			})
			.addCase(fetchMediaByCourse.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Fetch Media by Section
			.addCase(fetchMediaBySection.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(fetchMediaBySection.fulfilled, (state, action) => {
				state.isLoading = false;
				state.mediaList = action.payload;
			})
			.addCase(fetchMediaBySection.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Update Media
			.addCase(updateMedia.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(updateMedia.fulfilled, (state, action) => {
				state.isLoading = false;
				const index = state.mediaList.findIndex((m) => m.id === action.payload.id);
				if (index !== -1) {
					state.mediaList[index] = action.payload;
				}
			})
			.addCase(updateMedia.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Delete Media
			.addCase(deleteMedia.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(deleteMedia.fulfilled, (state, action) => {
				state.isLoading = false;
				state.mediaList = state.mediaList.filter((m) => m.id !== action.payload);
			})
			.addCase(deleteMedia.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Fetch Media Types
			.addCase(fetchMediaTypes.pending, (state) => {
				state.mediaTypesLoading = true;
			})
			.addCase(fetchMediaTypes.fulfilled, (state, action) => {
				state.mediaTypesLoading = false;
				state.mediaTypes = action.payload;
			})
			.addCase(fetchMediaTypes.rejected, (state, action) => {
				state.mediaTypesLoading = false;
			});
	},
});

export const { clearError, resetUploadState, setUploadProgress, setUploadStep } = mediaSlice.actions;
export default mediaSlice.reducer;
